import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronRight, Users, KeyRound, Handshake, X } from 'lucide-react';
import {
  getAllCompanyStaffById,
  getInsurerProfile,
  getIntermediariesByInsurer,
} from '../../../../../api/requests';
import useRoute from '../../../../../hooks/routesHook';
import useUserAccount from '../../../../../hooks/userAccountHook';

/**
 * Setup Checklist widget for the Insurer Overview Dashboard.
 *
 * Replaces the old forced-onboarding wizard (bank account + services + invite users).
 * Three cards, each fetches its own "done?" signal from an existing endpoint
 * (Option B: no new backend endpoint). Auto-hides when all three items are
 * complete OR the user dismisses it (persisted in localStorage).
 */
function SetupChecklist(): JSX.Element | null {
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const { user } = useUserAccount();
  const companyId = user?.company?.id;

  const dismissKey = `insurer_setup_checklist_dismissed_${companyId ?? 'unknown'}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(dismissKey) === 'true';
    } catch {
      return false;
    }
  });

  const { data: staff } = useQuery({
    queryKey: ['setupChecklist:staff', companyId],
    queryFn: () => getAllCompanyStaffById(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  const { data: insurerProfile } = useQuery({
    queryKey: ['setupChecklist:insurerProfile'],
    queryFn: () => getInsurerProfile(),
    staleTime: 60_000,
  });

  const { data: intermediaries } = useQuery({
    queryKey: ['setupChecklist:intermediaries', companyId],
    // getIntermediariesByInsurer takes a number; user.company.id is typed as string
    // (see other call sites, e.g. intermediariesHook.tsx:62 which wraps in Number()).
    queryFn: () => getIntermediariesByInsurer(Number(companyId)),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  const hasInvitedUsers = useMemo(() => {
    const list = Array.isArray(staff) ? staff : (staff as any)?.content ?? [];
    // Owner is auto-created on signup; count > 1 means at least one invited teammate.
    return list.length > 1;
  }, [staff]);

  const hasApiKeys = useMemo(() => {
    const p = insurerProfile as any;
    return Boolean(p?.developerSetting?.liveKey || p?.developerSetting?.testKey || p?.midApiKey);
  }, [insurerProfile]);

  const hasBrokerAccess = useMemo(() => {
    const list = (intermediaries as any)?.data?.content ?? (intermediaries as any)?.content ?? [];
    return Array.isArray(list) && list.length > 0;
  }, [intermediaries]);

  const items = [
    {
      id: 'invite-users',
      title: 'Invite Users',
      description: 'Add teammates to your insurer workspace.',
      icon: <Users className="w-5 h-5" />,
      done: hasInvitedUsers,
      cta: 'Invite',
      to: `${getDashboardBase()}/team`,
    },
    {
      id: 'api-keys',
      title: 'Set up API / MID keys',
      description: 'Configure your Developer keys to integrate with Brolly APIs.',
      icon: <KeyRound className="w-5 h-5" />,
      done: hasApiKeys,
      cta: 'Set up',
      to: `${getDashboardBase()}/settings?tab=developers`,
    },
    {
      id: 'broker-access',
      title: 'Broker Partner Access',
      description: 'Add or view brokers connected to your insurer workspace.',
      icon: <Handshake className="w-5 h-5" />,
      done: hasBrokerAccess,
      cta: hasBrokerAccess ? 'View' : 'Add',
      // Insurer's tab for managing broker partners is `brokers` (Underwriters ==
      // `insuranceCompanies` is a broker/internal-only tab; if you send an insurer
      // there, SettingsView doesn't find it in their filtered sections and falls
      // back to `profile`, which is what surfaced this bug).
      to: `${getDashboardBase()}/settings?tab=brokers`,
    },
  ];

  const allDone = items.every((i) => i.done);

  if (dismissed || allDone) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey, 'true');
    } catch {
      // localStorage may fail in private-browsing modes; state-only dismissal is fine.
    }
    setDismissed(true);
  };

  const doneCount = items.filter((i) => i.done).length;

  return (
    <div className="bg-white dark:bg-darkBg border border-gray-200 dark:border-darkBg-border rounded-2xl p-5 mb-4 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">
            Finish setting up your workspace
          </h2>
          <p className="text-sm text-gray-500 dark:text-darkBg-subText mt-1">
            {doneCount} of {items.length} complete
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label="Dismiss setup checklist"
          title="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <ul className="space-y-2">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 dark:border-darkBg-border hover:bg-gray-50 dark:hover:bg-darkBg-cardBg transition-colors"
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                item.done
                  ? 'bg-green-100 text-green-600'
                  : 'bg-primary-lighter text-primary-main'
              }`}
            >
              {item.done ? <Check className="w-5 h-5" /> : item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium ${
                  item.done
                    ? 'text-gray-400 dark:text-gray-500 line-through'
                    : 'text-gray-900 dark:text-darkBg-mainText'
                }`}
              >
                {item.title}
              </p>
              {!item.done && (
                <p className="text-xs text-gray-500 dark:text-darkBg-subText mt-0.5">
                  {item.description}
                </p>
              )}
            </div>
            {!item.done && (
              <button
                type="button"
                onClick={() => navigate(item.to)}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-main hover:text-primary-dark transition-colors"
              >
                {item.cta}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SetupChecklist;
